import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loader2 } from "lucide-react";

import Login from "./pages/Login";
import Register from "./pages/Register";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import AgencyOverview from "./pages/AgencyOverview";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Pipelines from "./pages/Pipelines";
import Automations from "./pages/Automations";
import AutomationBuilder from "./pages/AutomationBuilder";
import Conversations from "./pages/Conversations";
import Settings from "./pages/Settings";
import Snapshots from "./pages/Snapshots";
import Funnels from "./pages/Funnels";
import FunnelBuilder from "./pages/FunnelBuilder";
import PublicFunnel from "./pages/PublicFunnel";
import Calendars from "./pages/Calendars";
import Reports from "./pages/Reports";
import Broadcasts from "./pages/Broadcasts";
import Reputation from "./pages/Reputation";
import PublicBooking from "./pages/PublicBooking";
import PublicReview from "./pages/PublicReview";
import ReviewWidget from "./pages/ReviewWidget";
import Landing from "./pages/Landing";
import PublicReport from "./pages/PublicReport";
import Forms from "./pages/Forms";
import PublicForm from "./pages/PublicForm";
import AIChat from "./pages/AIChat";
import ChatWidget from "./pages/ChatWidget";
import Billing from "./pages/Billing";
import PaymentSuccess from "./pages/PaymentSuccess";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Calling from "./pages/Calling";
import SetPassword from "./pages/SetPassword";

function RootGate() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/f/:funnelId" element={<PublicFunnel />} />
            <Route path="/f/:funnelId/:pageSlug" element={<PublicFunnel />} />
            <Route path="/b/:calendarId" element={<PublicBooking />} />
            <Route path="/r/:subId" element={<PublicReview />} />
            <Route path="/widget/reviews/:subId" element={<ReviewWidget />} />
            <Route path="/report/:token" element={<PublicReport />} />
            <Route path="/form/:formId" element={<PublicForm />} />
            <Route path="/widget/chat/:subId" element={<ChatWidget />} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/" element={<RootGate />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="agency" element={<AgencyOverview />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/:contactId" element={<ContactDetail />} />
              <Route path="pipelines" element={<Pipelines />} />
              <Route path="automations" element={<Automations />} />
              <Route path="automations/:workflowId" element={<AutomationBuilder />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="snapshots" element={<Snapshots />} />
              <Route path="funnels" element={<Funnels />} />
              <Route path="forms" element={<Forms />} />
              <Route path="aichat" element={<AIChat />} />
              <Route path="funnels/:funnelId" element={<FunnelBuilder />} />
              <Route path="calendars" element={<Calendars />} />
              <Route path="reports" element={<Reports />} />
              <Route path="broadcasts" element={<Broadcasts />} />
              <Route path="reputation" element={<Reputation />} />
              <Route path="calls" element={<Calling />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
